/**
 * Damage System
 *
 * Handles damage application to heroes and turrets.
 */
/**
 * Apply damage to hero with artifact defensive effects (dodge, block) and weakness vulnerabilities
 * @returns object with actual damage taken and reflect damage to apply to attacker
 */
export function applyDamageToHero(_hero, _damage, _rng, _incomingDamageClass, _currentTick) {
    // Heroes are immortal in main gameplay; ignore incoming damage.
    return { damageTaken: 0, reflectDamage: 0 };
}
/**
 * Apply damage to a turret from enemy attack
 * @returns actual damage dealt
 */
export function applyDamageToTurret(turret, damage) {
    const actualDamage = Math.min(damage, turret.currentHp);
    turret.currentHp -= actualDamage;
    return actualDamage;
}
// ============================================================================
// PHYSICS-BASED DEFENSE INTEGRATIONS
// ============================================================================
import { calculateCCDuration, applyKnockbackWithResistance } from '../physics.js';
/**
 * Apply a CC effect to a hero with their CC resistance
 * @param hero - The hero to apply CC to
 * @param baseDuration - Base CC duration in ticks
 * @param ccResistance - Hero's CC resistance (0-0.9)
 * @param effectId - ID for tracking the effect (e.g., 'stun', 'freeze')
 * @param currentTick - Current game tick
 */
export function applyCCToHero(hero, baseDuration, ccResistance, effectId, currentTick) {
    const effectiveDuration = calculateCCDuration(baseDuration, ccResistance);
    // Don't apply if duration is 0 or negative
    if (effectiveDuration <= 0)
        return;
    // Add CC buff marker
    hero.buffs.push({
        id: effectId,
        stat: 'hpRegen', // Using hpRegen as marker (with 0 effect)
        amount: 0,
        expirationTick: currentTick + effectiveDuration,
    });
}
/**
 * Check if hero is currently CC'd
 * @param hero - Hero to check
 * @param effectId - CC effect ID to check for (e.g., 'stun', 'freeze')
 * @param currentTick - Current game tick
 */
export function isHeroCCd(hero, effectId, currentTick) {
    return hero.buffs.some(b => b.id === effectId && b.expirationTick > currentTick);
}
/**
 * Apply knockback to a hero with their knockback resistance
 * @param hero - Hero to knockback (must have PhysicsBody properties)
 * @param kbX - Knockback X velocity (fixed-point)
 * @param kbY - Knockback Y velocity (fixed-point)
 * @param knockbackResistance - Hero's knockback resistance (0-0.9)
 */
export function applyKnockbackToHero(hero, kbX, kbY, knockbackResistance) {
    applyKnockbackWithResistance(hero, kbX, kbY, knockbackResistance);
}
/**
 * Calculate incoming damage with fortress damage reduction
 * @param baseDamage - Base damage before reduction
 * @param incomingDamageReduction - Damage reduction (positive = less damage, negative = more damage)
 */
export function calculateFortressIncomingDamage(baseDamage, incomingDamageReduction) {
    // Clamp reduction to prevent negative damage or complete immunity
    const reduction = Math.min(Math.max(incomingDamageReduction, -1.0), 0.9);
    return Math.floor(baseDamage * (1 - reduction));
}
//# sourceMappingURL=damage.js.map